<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { MIN_PASSWORD_LENGTH, useAuth, type ChangePasswordResult } from '../composables/useAuth'
import BaseDialog from '../ui/components/BaseDialog.vue'
import BaseField from '../ui/components/BaseField.vue'
import { useDialog } from '../ui/dialogs'

const props = defineProps<{
  /**
   * `voluntary` — user-initiated from the menu; cancel button visible; current
   *   password always required.
   * `forced` — first-login change required by admin; the caller opens this with
   *   { dismissible: false }; the button reads "Sign out" instead of "Cancel";
   *   the current-password field is skipped unless Firebase's sensitive-
   *   operation window has lapsed and the update is rejected with
   *   `auth/requires-recent-login`.
   */
  mode: 'voluntary' | 'forced'
}>()

const { changePassword, changePasswordForced, logout, mustChangePassword } = useAuth()
const router = useRouter()
const { close } = useDialog()

const currentPassword = ref('')
const newPassword     = ref('')
const confirmPassword = ref('')
const needsReauth     = ref(false)
const busy            = ref(false)
const errorMsg        = ref<string | null>(null)

const requiresCurrent = computed(() => props.mode === 'voluntary' || needsReauth.value)

const clientError = computed(() => {
  if (!newPassword.value) return null
  if (newPassword.value.length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
  }
  if (confirmPassword.value && newPassword.value !== confirmPassword.value) {
    return 'Passwords do not match.'
  }
  return null
})

const canSubmit = computed(() => {
  if (busy.value) return false
  if (newPassword.value.length < MIN_PASSWORD_LENGTH) return false
  if (newPassword.value !== confirmPassword.value)    return false
  if (requiresCurrent.value && !currentPassword.value) return false
  return true
})

// Forced mode: if the flag flips false externally (Firestore listener sees
// clearMustChangePassword from another tab, say) auto-close so the user is not
// left staring at a stale gate. The success path in onSubmit also flips the
// flag and would trigger this same close — idempotent, both are fine.
watch(mustChangePassword, (must) => {
  if (!must && props.mode === 'forced') close()
})

function messageFor(result: Extract<ChangePasswordResult, { ok: false }>): string | null {
  if (result.step === 'reauth') {
    switch (result.code) {
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Incorrect current password.'
      case 'auth/too-many-requests':
        return 'Too many attempts. Wait a few minutes and try again.'
      case 'auth/network-request-failed':
        return 'Network error. Check your connection and try again.'
      default:
        return 'Could not verify your current password. Try again.'
    }
  }
  if (result.step === 'update') {
    switch (result.code) {
      case 'auth/weak-password':
        return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
      case 'auth/network-request-failed':
        return 'Network error. Check your connection and try again.'
      default:
        return 'Could not update your password. Try again.'
    }
  }
  // Flag write failed — the password itself changed successfully, so present
  // it that way. The listener will re-show this dialog on next sign-in, which
  // is annoying but preserves correctness.
  return null
}

async function onSubmit() {
  if (!canSubmit.value) return
  busy.value      = true
  errorMsg.value  = null

  let result: ChangePasswordResult
  if (requiresCurrent.value) {
    result = await changePassword(currentPassword.value, newPassword.value)
  } else {
    result = await changePasswordForced(newPassword.value)
    // Firebase says the login is too old for a sensitive op — reveal the
    // current-password field and let the user retry via the voluntary path.
    if (!result.ok && result.step === 'reauth' && result.code === 'needs-reauth') {
      needsReauth.value = true
      busy.value        = false
      errorMsg.value    = 'For security, please confirm your current password.'
      return
    }
  }

  busy.value = false

  if (result.ok) {
    close()
    return
  }

  const msg = messageFor(result)
  if (msg === null) {
    // Password changed, flag clear failed. Close anyway.
    close()
    return
  }
  errorMsg.value = msg
}

async function onSignOut() {
  await logout()
  // Router guard only runs on navigation, so signing out from /app leaves the
  // user on that route even after auth flips. Push them to landing explicitly.
  await router.replace('/')
  close()
}
</script>

<template>
  <BaseDialog :title="mode === 'forced' ? 'Set a new password' : 'Change password'" @dismiss="mode === 'voluntary' && close()">
    <p v-if="mode === 'forced'" class="hint">
      Your account was created with a temporary password. Choose a new one to
      continue.
    </p>

    <form id="change-password-form" @submit.prevent="onSubmit">
      <BaseField v-if="requiresCurrent" label="Current password">
        <template #default="{ id }">
          <input
            :id="id"
            v-model="currentPassword"
            type="password"
            autocomplete="current-password"
            required
            :disabled="busy"
            class="input"
          />
        </template>
      </BaseField>

      <BaseField label="New password">
        <template #default="{ id }">
          <input
            :id="id"
            v-model="newPassword"
            type="password"
            autocomplete="new-password"
            :minlength="MIN_PASSWORD_LENGTH"
            required
            :disabled="busy"
            class="input"
          />
        </template>
      </BaseField>

      <BaseField label="Confirm new password" :hint="`Must be at least ${MIN_PASSWORD_LENGTH} characters.`">
        <template #default="{ id }">
          <input
            :id="id"
            v-model="confirmPassword"
            type="password"
            autocomplete="new-password"
            :minlength="MIN_PASSWORD_LENGTH"
            required
            :disabled="busy"
            class="input"
          />
        </template>
      </BaseField>

      <p v-if="errorMsg" class="field-error" role="alert">{{ errorMsg }}</p>
      <p v-else-if="clientError" class="soft-warn">{{ clientError }}</p>
    </form>

    <template #actions>
      <button
        v-if="mode === 'voluntary'"
        type="button"
        class="btn btn-secondary"
        :disabled="busy"
        @click="close()"
      >Cancel</button>
      <button
        v-else
        type="button"
        class="btn btn-secondary"
        :disabled="busy"
        @click="onSignOut"
      >Sign out</button>
      <button type="submit" form="change-password-form" class="btn btn-primary" :disabled="!canSubmit">
        {{ busy ? 'Updating…' : 'Update password' }}
      </button>
    </template>
  </BaseDialog>
</template>

<style scoped>
/* Client-side validation warning shown before submission. Softer than
   field-error (which is reserved for the server's rejection). */
.soft-warn {
  margin: 0;
  font-size: 0.8rem;
  color: var(--danger);
}

form {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}
</style>
